import type { ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Fehler, Laden, Leer } from '../components/common/States'
import { useApiInfo, useHealth, useRegime, useStatus } from '../hooks/useQualyz'
import { ApiError } from '../lib/api'
import { formatDatum, formatDezimal, formatZahl } from '../lib/lenses'
import { regimeAnzeige } from '../lib/regime'

export default function SystemStatusPage() {
  const { isAdmin, isLoading } = useAuth()
  if (isLoading) return null
  if (!isAdmin) {
    return (
      <div className="q-page">
        <section className="q-card">
          <Leer text="Dieser Bereich ist Administratoren vorbehalten." />
        </section>
      </div>
    )
  }
  return <Status />
}

function Zeile({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="q-dl-row">
      <dt className="q-dl-k">{k}</dt>
      <dd className="q-dl-v">{v}</dd>
    </div>
  )
}

function tage(wert: number | null | undefined): string {
  if (typeof wert !== 'number') return '–'
  return wert === 1 ? '1 Tag' : `${wert} Tage`
}

const PRUEFUNG: Record<string, string> = {
  redis: 'Redis',
  database: 'Datenbank',
  daten_aktuell: 'Daten aktuell',
}

const BAUSTEIN: Record<string, string> = {
  trend: 'Trend',
  vix: 'VIX',
  yield_curve: 'Zinskurve',
  credit_spread: 'Kredit-Spread',
}

function Status() {
  const health = useHealth()
  const regime = useRegime()
  const status = useStatus()
  const info = useApiInfo()

  return (
    <div className="q-page">
      <div className="q-grid2">
        <section className="q-card">
          <div className="q-card-head">
            <h2 className="q-card-title">Dienste</h2>
            {health.data && (
              <span
                className={`q-badge ${
                  health.data.status === 'healthy'
                    ? 'q-badge--ok'
                    : health.data.status === 'stale'
                      ? 'q-badge--warn'
                      : 'q-badge--bad'
                }`}
              >
                {health.data.status}
              </span>
            )}
          </div>
          {health.isPending ? (
            <Laden text="Prüfe …" />
          ) : health.isError ? (
            <Fehler fehler={health.error} nochmal={() => health.refetch()} />
          ) : (
            <dl className="q-dl">
              {Object.entries(health.data.checks ?? {}).map(([name, ok]) => (
                <Zeile key={name} k={PRUEFUNG[name] ?? name} v={ok ? 'ok' : 'Fehler'} />
              ))}
              <Zeile k="Kurse, Alter" v={tage(health.data.daten?.kurse_tage_alt)} />
              <Zeile k="Fundamentaldaten, Alter" v={tage(health.data.daten?.fundamentaldaten_tage_alt)} />
              {health.data.daten?.hinweis && <Zeile k="Hinweis" v={health.data.daten.hinweis} />}
            </dl>
          )}
        </section>

        <section className="q-card">
          <div className="q-card-head">
            <h2 className="q-card-title">Marktregime</h2>
          </div>
          {regime.isPending ? (
            <Laden text="Lade …" />
          ) : regime.isError ? (
            <Fehler fehler={regime.error} nochmal={() => regime.refetch()} />
          ) : (
            <dl className="q-dl">
              <Zeile k="Regime" v={`${regimeAnzeige(regime.data.regime).kurz} · ${regimeAnzeige(regime.data.regime).phase}`} />
              <Zeile k="Score" v={`${formatDezimal(regime.data.regime.score_0_100, 1)} / 100`} />
              <Zeile k="Positionsfaktor" v={formatDezimal(regime.data.regime.position_size_factor, 1)} />
              {Object.entries(regime.data.regime.components).map(([name, wert]) => (
                <Zeile key={name} k={BAUSTEIN[name] ?? name} v={formatDezimal(wert, 2)} />
              ))}
              <Zeile k="Stand" v={formatDatum(regime.data.snapshot.date)} />
              <Zeile k="Quelle" v={regime.data.source ?? '–'} />
            </dl>
          )}
        </section>

        <section className="q-card">
          <div className="q-card-head">
            <h2 className="q-card-title">Bestand</h2>
          </div>
          {status.isPending ? (
            <Laden text="Lade …" />
          ) : status.isError ? (
            status.error instanceof ApiError && status.error.status === 404 ? (
              <Leer text="/api/status ist erst nach dem nächsten Backend-Neustart verfügbar." />
            ) : (
              <Fehler fehler={status.error} nochmal={() => status.refetch()} />
            )
          ) : (
            <dl className="q-dl">
              <Zeile k="Universum (handelbar)" v={formatZahl(status.data.universum)} />
              <Zeile k="Vergleichsgruppe (delistet)" v={formatZahl(status.data.vergleichsgruppe)} />
              <Zeile k="Kurse Stand" v={formatDatum(status.data.kurse_stand)} />
              <Zeile
                k="Rückvergleich"
                v={
                  status.data.rueckvergleich
                    ? `${status.data.rueckvergleich.stichtage} Stichtage bis ${formatDatum(status.data.rueckvergleich.bis)}`
                    : '–'
                }
              />
            </dl>
          )}
        </section>

        <section className="q-card">
          <div className="q-card-head">
            <h2 className="q-card-title">API</h2>
          </div>
          {info.isPending ? (
            <Laden text="Lade …" />
          ) : info.isError ? (
            <Fehler fehler={info.error} nochmal={() => info.refetch()} />
          ) : (
            <dl className="q-dl">
              <Zeile k="Umgebung" v={info.data.environment} />
              <Zeile k="Version" v={info.data.version} />
              <Zeile k="Ticker gesamt" v={formatZahl(info.data.stats.tickers)} />
              <Zeile k="Nutzer" v={formatZahl(info.data.stats.users)} />
              <Zeile k="Bezahl-API freigeschaltet" v={info.data.paid_api_enabled ? 'ja' : 'nein'} />
            </dl>
          )}
        </section>
      </div>
    </div>
  )
}
